(() => {
  if (customElements.get('rays-flavour-gallery')) return;
  const played = new Set();

  class RaysFlavourGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.states = new Map();
      this.querySelectorAll('[data-artwork]').forEach(artwork => {
        const card = artwork.closest('.flavour');
        const key = card.getAttribute('aria-labelledby');
        this.states.set(artwork, {
          key, video: null, started: false,
          finished: played.has(key) || !artwork.dataset.videoSrc,
          visible: false
        });
      });

      if ('IntersectionObserver' in window) {
        this.preloadObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            this.prepare(entry.target);
            this.preloadObserver.unobserve(entry.target);
          });
        }, { rootMargin: '400px 0px' });
        this.revealObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const state = this.states.get(entry.target);
            state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
            if (state.visible) this.reveal(entry.target);
          });
        }, { threshold: [0, 0.2] });
        this.states.forEach((state, artwork) => {
          this.preloadObserver.observe(artwork);
          this.revealObserver.observe(artwork);
        });
      }

      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.states.forEach((state, artwork) => this.finish(artwork));
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.states.forEach((state, artwork) => {
          if (document.hidden && state.started) this.finish(artwork);
          else if (!document.hidden && state.visible) this.reveal(artwork);
        });
      }, { signal });
    }

    prepare(artwork) {
      const state = this.states.get(artwork);
      if (state.video || state.finished || this.reducedMotion.matches) return;
      const video = document.createElement('video');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.disablePictureInPicture = true;
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      // URLs come from Shopify's asset_url filter, never relative filesystem paths.
      video.src = artwork.dataset.videoSrc;
      const { signal } = this.controller;
      video.addEventListener('playing', () => {
        if (state.finished || this.reducedMotion.matches || document.hidden) {
          this.finish(artwork);
          return;
        }
        artwork.classList.add('is-playing');
      }, { signal });
      video.addEventListener('ended', () => this.finish(artwork), { signal });
      video.addEventListener('error', () => this.finish(artwork), { signal });
      state.video = video;
      artwork.append(video);
    }

    async reveal(artwork) {
      const state = this.states.get(artwork);
      if (state.started || state.finished || document.hidden) return;
      state.started = true;
      played.add(state.key);
      if (this.reducedMotion.matches) {
        this.finish(artwork);
        return;
      }
      this.prepare(artwork);
      try {
        await state.video.play();
      } catch {
        // Leave the finished illustration visible when autoplay is blocked.
        this.finish(artwork);
      }
    }

    finish(artwork) {
      const state = this.states.get(artwork);
      state.finished = true;
      played.add(state.key);
      state.video?.pause();
      artwork.classList.remove('is-playing');
    }

    disconnectedCallback() {
      this.preloadObserver?.disconnect();
      this.revealObserver?.disconnect();
      this.controller?.abort();
      this.states?.forEach((state, artwork) => {
        state.video?.pause();
        state.video?.remove();
        artwork.classList.remove('is-playing');
      });
      this.controller = null;
    }
  }

  customElements.define('rays-flavour-gallery', RaysFlavourGallery);
})();
